import { useLiveQuery } from 'dexie-react-hooks';
import type { Shift } from '@sense/shared';
import { currentCashier } from './cashier';
import { db } from './db';
import { afterQueued } from './sync';

export const DENOMINATIONS = [2000, 500, 200, 100, 50, 20, 10, 5, 2, 1]; // rupees

export function useOpenShift(): Shift | null | undefined {
  return useLiveQuery(async () => (await db.shifts.filter((s) => s.closed_at == null).first()) ?? null, []);
}

async function queue(shift: Shift) {
  await db.transaction('rw', db.shifts, db.outbox, async () => {
    await db.shifts.put(shift);
    await db.outbox.add({ kind: 'shift', doc: shift });
  });
  void afterQueued();
}

export async function openShift(float: number): Promise<Shift> {
  const shift: Shift = {
    id: crypto.randomUUID(), opened_at: Date.now(), opened_by: currentCashier().name, float, moves: [],
    closed_at: null, closed_by: null, counted: null, expected: null, denominations: null, totals: null,
  };
  await queue(shift);
  return shift;
}

export async function addCashMove(shift: Shift, amount: number, reason: string) {
  await queue({ ...shift, moves: [...shift.moves, [Date.now(), amount, reason]] });
}

/** What this terminal took since the shift opened, from its own bills (works offline). */
export async function shiftTotals(shift: Shift) {
  const bills = await db.bills.where('ts').aboveOrEqual(shift.opened_at).toArray();
  const byMethod: Record<string, number> = {};
  let sales = 0, returns = 0;
  for (const b of bills) {
    const sign = b.kind === 1 ? -1 : 1;
    if (b.kind === 1) returns += b.total; else sales += b.total;
    for (const [m, a] of b.payments) byMethod[m] = (byMethod[m] ?? 0) + sign * a;
  }
  const moves = shift.moves.reduce((a, [, amt]) => a + amt, 0);
  const expected = shift.float + (byMethod.cash ?? 0) + moves;
  return { bills: bills.length, sales, returns, byMethod, moves, expected };
}

export async function closeShift(shift: Shift, denominations: Record<string, number>): Promise<Shift> {
  const t = await shiftTotals(shift);
  const counted = Object.entries(denominations).reduce((a, [d, n]) => a + Number(d) * 100 * n, 0);
  const closed: Shift = {
    ...shift, closed_at: Date.now(), closed_by: currentCashier().name, counted, expected: t.expected, denominations,
    totals: { bills: t.bills, sales: t.sales, returns: t.returns, byMethod: t.byMethod },
  };
  await queue(closed);
  return closed;
}
